// Smart initialization that preserves theme data
(function() {
  'use strict';
  
  // Check if we need to initialize or just reload functions
  const isFirstLoad = !window.wickProProgram || !window.wickProProgram.initialized;

  // Initialize or extend the namespace
  if (!window.wickProProgram) {
    window.wickProProgram = {};
  }

  // Preserve existing theme data and extend with new properties
  Object.assign(window.wickProProgram, {
    initialized: true,
    
    // Data containers (don't overwrite if they exist)
    customerData: window.wickProProgram.customerData || {
      isProCustomer: false,
      proLevel: null,
      proExpiration: null,
      isProActive: false
    },
    
    proLevels: window.wickProProgram.proLevels || [],
    productAdjustments: window.wickProProgram.productAdjustments || {},
    originalPrices: window.wickProProgram.originalPrices || new Map(),
    
    // Configuration
    config: {
      version: '1.0.0',
      debug: false, // Enable debug logging
      // CSS selectors for your specific theme structure
      priceSelectors: {
        current: 'sale-price, .sale-price, .price, .product-price, [data-price], .price-item, .money, .current-price',
        compare: 'compare-at-price, .compare-at-price, .original-price, .was-price, .line-through', 
        container: '.price-list, [data-block-type="price"], .product-price-container, .price-container'
      },
      
      // CSS classes for styling
      classes: {
        proPrice: 'pro-price',
        proBadge: 'pro-badge',
        originalPrice: 'price--original'
      }
    },
    
    // Utility functions
    utils: {
      /**
       * Parse price from text
       */
      parsePrice: function(priceText) {
        if (!priceText) return 0;
        const cleaned = priceText.toString().replace(/[^0-9.]/g, '');
        return parseFloat(cleaned) || 0;
      },
      
      /**
       * Format price for display
       */
      formatPrice: function(price) {
        try {
          const currency = window.Shopify?.currency?.active || 'USD';
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
          }).format(price / 100);
        } catch (error) {
          // Fallback formatting
          return '$' + (price / 100).toFixed(2);
        }
      },
      
      /**
       * Check if a date has expired
       */
      isExpired: function(dateString) {
        if (!dateString) return false;
        
        try {
          const now = Math.floor(Date.now() / 1000);
          const expirationTimestamp = Math.floor(new Date(dateString).getTime() / 1000);
          
          return expirationTimestamp < now;
        } catch (error) {
          console.error('Error parsing date:', error);
          return false;
        }
      },
      
      /**
       * Debug logger
       */
      log: function(...args) {
        if (window.wickProProgram.config.debug) {
          console.log('[Wick Pro Program]', ...args);
        }
      },
      
      /**
       * Normalize level names for consistent comparison
       */
      normalizeLevelName: function(levelName) {
        return (levelName || '').toLowerCase().trim();
      },
      
      /**
       * Generate unique selector for price elements
       */
      getPriceElementId: function(element, prefix) {
        const allElements = Array.from(document.querySelectorAll(window.wickProProgram.config.priceSelectors.current));
        return `${prefix}-${allElements.indexOf(element)}`;
      }
    }
  });

  // Configuration shortcuts
  const CONFIG = window.wickProProgram.config;
  const utils = window.wickProProgram.utils;

  // Data shortcuts
  let customerData = window.wickProProgram.customerData;
  let originalPrices = window.wickProProgram.originalPrices;
  let proLevels = window.wickProProgram.proLevels;
  let productAdjustments = window.wickProProgram.productAdjustments;

  /**
   * Initialize the Pro pricing system
   */
  async function init() {
    try {
      // utils.log('Initializing Pro pricing system');
      
      // Load customer data
      await loadCustomerData();
      
      // Load pro levels and product adjustments
      await loadProLevelsData();
      await loadProductAdjustments();
      
      // utils.log('Data loaded. Customer:', customerData, 'Pro Levels:', proLevels);
      // utils.log('Product data available:', !!window.wickProProgram.productData);
      // utils.log('Theme data available:', {
      //   customer: !!window.wickProProgram.customerDataFromTheme,
      //   levels: !!window.wickProProgram.proLevelsFromTheme,
      //   adjustments: !!window.wickProProgram.productAdjustmentsFromTheme
      // });
      
      // Only proceed if customer is a Pro customer AND their membership is active
      if (!customerData.isProCustomer || !customerData.isProActive) {
        if (!customerData.isProCustomer) {
          // utils.log('Customer is not a Pro customer');
        } else {
          // utils.log('Customer Pro membership has expired');
        }
        return;
      }
      
      // utils.log('Customer is active Pro member, applying pricing');
      
      // Store original prices
      storeOriginalPrices();
      
      // Apply initial Pro pricing
      updateProPricing();
      
      // Add Pro badge
      addProBadge();
      
      // utils.log('Pro pricing system initialized successfully');
    } catch (error) {
      console.error('Error initializing Pro pricing:', error);
    }
  }

  /**
   * Load customer data from theme variables
   */
  async function loadCustomerData() {
    try {
      // utils.log('Loading customer data');
      
      if (window.wickProProgram?.customerDataFromTheme) {
        const themeData = window.wickProProgram.customerDataFromTheme;
        
        const isProActive = themeData.isProCustomer && !utils.isExpired(themeData.proExpiration);

        customerData.isProCustomer = themeData.isProCustomer;
        customerData.proLevel = themeData.proLevel;
        customerData.proExpiration = themeData.proExpiration;
        customerData.isProActive = isProActive;

        // utils.log('Customer data loaded:', customerData);
        return;
      }

      // utils.log('No customer data found in theme');
    } catch (error) {
      console.error('Error loading customer data:', error);
    }
  }

  /**
   * Load pro levels data from shop metafields
   */
  async function loadProLevelsData() {
    try {
      if (window.wickProProgram?.proLevelsFromTheme) {
        if (typeof window.wickProProgram.proLevelsFromTheme === 'string') {
          proLevels = JSON.parse(window.wickProProgram.proLevelsFromTheme);
        } else {
          proLevels = window.wickProProgram.proLevelsFromTheme;
        }
        window.wickProProgram.proLevels = proLevels;
        // utils.log('Pro levels loaded:', proLevels);
        return;
      }

      // utils.log('No pro levels data found');
    } catch (error) {
      console.error('Error loading pro levels:', error);
      proLevels = [];
    }
  }

  /**
   * Load product-specific adjustments
   */
  async function loadProductAdjustments() {
    try {
      if (window.wickProProgram?.productAdjustmentsFromTheme) {
        if (typeof window.wickProProgram.productAdjustmentsFromTheme === 'string') {
          productAdjustments = JSON.parse(window.wickProProgram.productAdjustmentsFromTheme);
        } else {
          productAdjustments = window.wickProProgram.productAdjustmentsFromTheme;
        }
        window.wickProProgram.productAdjustments = productAdjustments;
        // utils.log('Product adjustments loaded:', productAdjustments);
        return;
      }

      // utils.log('No product adjustments found');
      productAdjustments = {};
    } catch (error) {
      console.error('Error loading product adjustments:', error);
      productAdjustments = {};
    }
  }

  /**
   * Store original prices for calculations
   */
  function storeOriginalPrices() {
    // utils.log('Storing original prices');
    
    const priceElements = document.querySelectorAll(CONFIG.priceSelectors.current);
    // utils.log('Found price elements:', priceElements);
    
    priceElements.forEach((element, index) => {
      // Try to get price from data attribute first, then text content
      let price = parseFloat(element.dataset.originalPrice);
      
      if (!price || isNaN(price)) {
        // For custom elements like <sale-price>, get the text content excluding hidden spans
        let priceText = element.textContent.trim();
        
        // Remove "Sale price" or "Regular price" screen reader text
        priceText = priceText.replace(/^(Sale price|Regular price)\s*/i, '');
        
        price = utils.parsePrice(priceText);
        // utils.log(`Extracted price from element ${index}: "${priceText}" -> ${price}`);
      }
      
      if (price > 0) {
        const elementId = utils.getPriceElementId(element, 'current');
        originalPrices.set(elementId, price);
        element.dataset.originalPrice = price;
        // utils.log(`Stored price for element ${elementId}:`, price);
      }
    });

    const comparePriceElements = document.querySelectorAll(CONFIG.priceSelectors.compare);
    // utils.log('Found compare price elements:', comparePriceElements);
    
    comparePriceElements.forEach((element, index) => {
      let price = parseFloat(element.dataset.originalComparePrice);
      
      if (!price || isNaN(price)) {
        // For hidden elements or elements showing $0, try to get from variant data
        if (window.wickProProgram?.productData?.variants) {
          // Try to get current variant's compare_at_price
          const currentVariantId = getCurrentVariantId();
          if (currentVariantId) {
            const variant = window.wickProProgram.productData.variants.find(v => v.id == currentVariantId);
            if (variant && variant.compare_at_price > 0) {
              price = variant.compare_at_price / 100; // Convert from cents
              // utils.log(`Got compare price from variant data: ${price}`);
            }
          }
        }
        
        // Fallback: try to parse text content
        if (!price || isNaN(price)) {
          let priceText = element.textContent.trim();
          
          // Remove "Regular price" screen reader text
          priceText = priceText.replace(/^(Regular price|Compare at)\s*/i, '');
          
          // Skip if it's $0 or empty
          const parsedPrice = utils.parsePrice(priceText);
          if (parsedPrice > 0) {
            price = parsedPrice;
            // utils.log(`Extracted compare price from element ${index}: "${priceText}" -> ${price}`);
          }
        }
      }
      
      if (price > 0) {
        const elementId = utils.getPriceElementId(element, 'compare');
        originalPrices.set(elementId, price);
        element.dataset.originalComparePrice = price;
        // utils.log(`Stored compare price for element ${elementId}:`, price);
      }
    });
  }
  
  /**
   * Helper function to get current variant ID
   */
  function getCurrentVariantId() {
    // Try multiple ways to get current variant
    const variantInput = document.querySelector('[name="id"]');
    if (variantInput) {
      return variantInput.value;
    }
    
    // Fallback: get first variant if available
    if (window.wickProProgram?.productData?.variants?.length > 0) {
      return window.wickProProgram.productData.variants[0].id;
    }
    
    return null;
  }

  /**
   * Calculate Pro discount for current customer
   */
  function calculateProDiscount(basePrice, compareAtPrice) {
    if (!customerData.isProCustomer || !customerData.isProActive || !customerData.proLevel) {
      return { discountedPrice: basePrice, discount: 0 };
    }

    const price = parseFloat(basePrice) || 0;
    const compareAt = parseFloat(compareAtPrice) || price;

    // Find the base discount % for the customer's level
    const normalizedProLevel = utils.normalizeLevelName(customerData.proLevel);
    let proDiscount = 0;

    for (const level of proLevels) {
      const levelName = utils.normalizeLevelName(level.name);
      if (levelName === normalizedProLevel) {
        proDiscount = level.discount;
        break;
      }
    }

    // utils.log(`Base discount for level ${customerData.proLevel}:`, proDiscount);

    // Handle per-product adjustments
    let levelAdjustment = null;
    for (const [key, value] of Object.entries(productAdjustments)) {
      const adjustmentKey = utils.normalizeLevelName(key);
      if (adjustmentKey === normalizedProLevel) {
        levelAdjustment = value;
        break;
      }
    }

    // Apply adjustment to base discount
    if (levelAdjustment) {
      // utils.log('Applying adjustment:', levelAdjustment);
      
      if (levelAdjustment.adjustment === 'deny') {
        proDiscount = 0;
      } else if (levelAdjustment.adjustment === 'add') {
        proDiscount = proDiscount + levelAdjustment.value;
      } else if (levelAdjustment.adjustment === 'subtract') {
        proDiscount = Math.max(0, proDiscount - levelAdjustment.value);
      }
    }

    if (proDiscount <= 0) {
      return { discountedPrice: price, discount: 0 };
    }

    // Calculate the discounted price
    const priceForDiscount = compareAt;
    const discountAmount = (priceForDiscount * proDiscount) / 100;
    const discountedPrice = priceForDiscount - discountAmount;
    
    // Only apply if discounted price is less than current price
    const finalPrice = discountedPrice < price ? discountedPrice : price;

    // utils.log(`Calculated discount: ${proDiscount}%, Final price: ${finalPrice}`);

    return { 
      discountedPrice: finalPrice, 
      discount: proDiscount,
      originalPrice: price,
      compareAtPrice: compareAt
    };
  }

  /**
   * Update pricing with Pro discounts
   */
  function updateProPricing() {
    // utils.log('Updating Pro pricing');
    
    const currentPriceElements = document.querySelectorAll(CONFIG.priceSelectors.current);
    // utils.log('Found price elements to update:', currentPriceElements);
    
    currentPriceElements.forEach((element, index) => {
      const elementId = utils.getPriceElementId(element, 'current');
      const originalPrice = parseFloat(element.dataset.originalPrice) || originalPrices.get(elementId);
      
      // Get compare at price if available
      const comparePriceElements = document.querySelectorAll(CONFIG.priceSelectors.compare);
      const compareElement = comparePriceElements[index];
      let originalComparePrice = originalPrice;
      
      if (compareElement) {
        const compareElementId = utils.getPriceElementId(compareElement, 'compare');
        originalComparePrice = parseFloat(compareElement.dataset.originalComparePrice) || 
                             originalPrices.get(compareElementId) || 
                             originalPrice;
      }
      
      // utils.log(`Processing element ${index}: original=${originalPrice}, compare=${originalComparePrice}`);
      
      if (originalPrice > 0) {
        const result = calculateProDiscount(originalPrice, originalComparePrice);
        
        // Determine if there's a sale happening (any discount from compare price)
        const isOnSale = result.discount > 0 || originalComparePrice > originalPrice;
        const finalDisplayPrice = result.discount > 0 ? result.discountedPrice : originalPrice;
        
        // For custom elements, we need to update the text content while preserving screen reader text
        const srText = element.querySelector('.sr-only')?.textContent || '';
        const newPriceText = utils.formatPrice(finalDisplayPrice * 100);
        
        if (srText) {
          element.innerHTML = `<span class="sr-only">${srText}</span>${newPriceText}`;
        } else {
          element.textContent = newPriceText;
        }
        
        // Manage the text-on-sale class
        if (isOnSale) {
          // Add text-on-sale class when there's any kind of sale
          element.classList.add('text-on-sale');
          if (result.discount > 0) {
            element.classList.add(CONFIG.classes.proPrice);
          }
        } else {
          // Remove text-on-sale class when there's no sale
          element.classList.remove('text-on-sale');
          element.classList.remove(CONFIG.classes.proPrice);
        }
        
        // Handle compare-at-price visibility and content
        if (compareElement) {
          if (isOnSale) {
            // Show compare price when there's any sale
            compareElement.removeAttribute('hidden');
            
            // Determine what price to show as compare-at
            let compareDisplayPrice = originalComparePrice;
            
            // If Pro discount is applied, show the higher of original or compare price
            if (result.discount > 0) {
              compareDisplayPrice = Math.max(originalPrice, originalComparePrice);
            }
            
            // Update compare price text
            const compareSrText = compareElement.querySelector('.sr-only')?.textContent || '';
            const comparePriceText = utils.formatPrice(compareDisplayPrice * 100);
            
            if (compareSrText) {
              compareElement.innerHTML = `<span class="sr-only">${compareSrText}</span>${comparePriceText}`;
            } else {
              compareElement.textContent = comparePriceText;
            }
            
            // utils.log(`Showing compare price: ${comparePriceText}`);
          } else {
            // Hide compare price when there's no sale
            compareElement.setAttribute('hidden', '');
            // utils.log(`Hiding compare price - no sale occurring`);
          }
        }
        
        if (result.discount > 0) {
          // utils.log(`Successfully applied Pro discount: ${result.discount}% - Final price: ${newPriceText}`);
        } else if (originalComparePrice > originalPrice) {
          // utils.log(`No Pro discount, but regular sale maintained - Price: ${newPriceText}`);
        } else {
          // utils.log(`No discount applied - Regular price: ${newPriceText}`);
        }
      } else {
        // utils.log(`Skipping element ${index} - no valid original price`);
      }
    });
  }

  /**
   * Add Pro customer badge
   */
  function addProBadge() {
    const containers = document.querySelectorAll(CONFIG.priceSelectors.container);
    let priceContainer = containers[0];
    
    // Fallback: find any price-related container
    if (!priceContainer) {
      priceContainer = document.querySelector('.price') || 
                      document.querySelector('.product-price') ||
                      document.querySelector('[data-price]');
                      
      if (priceContainer) {
        priceContainer = priceContainer.parentElement;
      }
    }
    
    if (priceContainer && !priceContainer.querySelector(`.${CONFIG.classes.proBadge}`)) {
      const badge = document.createElement('div');
      badge.className = CONFIG.classes.proBadge;
      
      let badgeContent = '';
      if (customerData.isProActive) {
        badgeContent = `
          <span style="
            display: inline-block;
            background: #2c5aa0;
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
            margin-left: 8px;
          ">
            PRO ${customerData.proLevel ? customerData.proLevel.toUpperCase() : ''} PRICING
          </span>
        `;
        
        if (customerData.proExpiration) {
          const expirationDate = new Date(customerData.proExpiration);
          badgeContent += `
            <span style="
              display: block;
              font-size: 10px;
              color: #666;
              margin-top: 2px;
            ">
              Valid until ${expirationDate.toLocaleDateString()}
            </span>
          `;
        }
      }
      
      badge.innerHTML = badgeContent;
      priceContainer.appendChild(badge);
    }
  }

  /**
   * Handle variant change events
   */
  function handleVariantChange(event) {
    // utils.log('Handling variant change, event:', event);
    
    if (event && window.wickProProgram?.productData?.variants) {
      const selectedVariant = window.wickProProgram.productData.variants.find(v => v.id == event.detail.variant.id);
      // utils.log('Selected variant:', selectedVariant);
      
      if (selectedVariant) {
        // Update original price data attributes
        // IMPORTANT: Convert from cents to dollars
        const priceElements = document.querySelectorAll(CONFIG.priceSelectors.current);
        priceElements.forEach(element => {
          element.dataset.originalPrice = selectedVariant.price / 100;  // Convert cents to dollars
          if (selectedVariant.compare_at_price > 0) {
            element.dataset.originalComparePrice = selectedVariant.compare_at_price / 100;  // Convert cents to dollars
          }
        });
        
        // Also update compare price elements
        const comparePriceElements = document.querySelectorAll(CONFIG.priceSelectors.compare);
        comparePriceElements.forEach(element => {
          if (selectedVariant.compare_at_price > 0) {
            element.dataset.originalComparePrice = selectedVariant.compare_at_price / 100;  // Convert cents to dollars
          }
        });
        
        // utils.log('Updated price data attributes for variant:', {
        //   price: selectedVariant.price / 100,
        //   compareAtPrice: selectedVariant.compare_at_price / 100
        // });
      }
    }
    
    // Always try to update pro pricing after a delay
    setTimeout(() => {
      if (customerData.isProActive) {
        storeOriginalPrices();
        updateProPricing();
      }
    }, 200);
  }


  // Assign core functions to namespace (always update these)
  window.wickProProgram.init = init;
  window.wickProProgram.loadCustomerData = loadCustomerData;
  window.wickProProgram.loadProLevelsData = loadProLevelsData;
  window.wickProProgram.loadProductAdjustments = loadProductAdjustments;
  window.wickProProgram.updateProPricing = updateProPricing;
  window.wickProProgram.calculateProDiscount = calculateProDiscount;
  window.wickProProgram.addProBadge = addProBadge;
  window.wickProProgram.handleVariantChange = handleVariantChange;
  window.wickProProgram.storeOriginalPrices = storeOriginalPrices;
  window.wickProProgram.getCurrentVariantId = getCurrentVariantId;

  // Initialize when DOM is ready (only if first load)
  function initWhenReady() {
    // console.log('Initializing Wick Pro Program...');
    init();
  }

  if (isFirstLoad) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initWhenReady);
    } else {
      // DOM already loaded, initialize with a small delay
      setTimeout(initWhenReady, 100);
    }
  } else {
    // Just update functions, don't re-initialize
    console.log('Functions updated, ready to use');
  }

  console.log('Wick Pro Program script loaded successfully');

})();
